/*
 * Copyright (c) 2018 Well Solutions SRL. All Rights Reserved.
 * This software is the proprietary information of Well Solutions SRL.
 * Use is subject to license and non-disclosure terms
 */

import { HttpClient, HttpContext, HttpContextToken, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { JSONable } from './jsonable';
import { map } from 'rxjs/operators';
import { JsonApiResource } from './json-api-resource';
import { Injectable } from '@angular/core';
import { SpecificationQueryParamsEventModel } from "./specification-query-params-event.model";
import { AdditionalParams } from "./query-params/AdditionalParams";
import { QueryParams } from "./query-params/query-params";

export const SKIP_UI_BLOCKER = new HttpContextToken<boolean>(() => false);

@Injectable()
export abstract class AbstractResourceService<V, D, MV, MD> {
  protected url!: string;
  protected httpClient!: HttpClient;
  protected headers = new HttpHeaders({
    'Content-Type': 'application/json; charset=utf-8',
  });

  protected vType?: new () => V & JSONable;
  protected mvType?: new () => MV & JSONable;
  private readonly dType?: new () => D & JSONable;
  private readonly mdType?: new () => MD & JSONable;

  protected constructor(
    httpClient: HttpClient,
    url: string,
    vType?: new () => V & JSONable,
    dType?: new () => D & JSONable,
    mvType?: new () => MV & JSONable,
    mdType?: new () => MD & JSONable
  ) {
    this.httpClient = httpClient;
    this.url = url;
    this.vType = vType;
    this.mvType = mvType;
    this.dType = dType;
    this.mdType = mdType;
  }

  protected static resourceMapper<T>(type: (new () => T & JSONable) | undefined, resourceJSON: any): T {
    if (type) {
      const object = new type();
      if (object.fromJSON) {
        object.fromJSON(resourceJSON);
      }
      return object;
    }
    return resourceJSON;
  }

  getDtoMapper() {
    return (source: any) =>
      source.pipe(
        map((data: D) => {
          return data ? AbstractResourceService.resourceMapper(this.dType, data) : data;
        })
      );
  }

  getJsonApiMapper() {
    return (source: any) =>
      source.pipe(
        map((jsonApiResource: JsonApiResource<D, MD>) => {
          if (jsonApiResource) {
            jsonApiResource.data = AbstractResourceService.resourceMapper(this.dType, jsonApiResource.data);
            jsonApiResource.meta = AbstractResourceService.resourceMapper(this.mdType, jsonApiResource.meta);
          }
          return jsonApiResource;
        })
      );
  }

  getTemplate(path = '', query = ''): Observable<JsonApiResource<D, MD>> {
    return this.httpClient?.get(`${this.url}/new/template${path}?${query}`).pipe(this.getDtoMapper());
  }

  findOne(id: string): Observable<JsonApiResource<D, MD>> {
    return this.httpClient?.get(`${this.url}/${id}`, { headers: this.headers }).pipe(this.getDtoMapper());
  }

  findAll(query?: SpecificationQueryParamsEventModel, additionalParams?: Array<AdditionalParams>): Observable<JsonApiResource<V[], MV>> {
    const queryString = new QueryParams(query, additionalParams ? additionalParams : []).toString();

    return this.httpClient?.get(`${this.url}${queryString}`, { headers: this.headers }).pipe(this.mapToListOfObjects());
  }

  findAllPost(query?: SpecificationQueryParamsEventModel, additionalParams?: Array<AdditionalParams>): Observable<JsonApiResource<V[], MV>> {
    const qp = new QueryParams(query, additionalParams ?? []);
    const formHeaders = new HttpHeaders({ 'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8' });
    return this.httpClient
      ?.post(`${this.url}/list`, qp.toHttpParams().toString(), {
        headers: formHeaders,
        context: new HttpContext().set(SKIP_UI_BLOCKER, true),
      })
      .pipe(this.mapToListOfObjects());
  }

  mapToListOfObjects() {
    return (source: any) =>
      source.pipe(
        map((jsonApiResource: JsonApiResource<V[], MV>) => {
          jsonApiResource.data = jsonApiResource.data.map((item) =>
            AbstractResourceService.resourceMapper(this.vType, item)
          );
          jsonApiResource.meta = AbstractResourceService.resourceMapper(this.mvType, jsonApiResource.meta);
          return jsonApiResource;
        })
      );
  }

  /**
   * @deprecated Use saveDtoResponse or saveJsonApiResponse or your own implementation; This method will be protected in the future;
   * @param resource
   */
  save(resource: D): any {
    return this.httpClient?.post(`${this.url}`, resource, { headers: this.headers })
  }

  saveDtoResponse(resource: D): Observable<D> {
    return this.save(resource).pipe(this.getDtoMapper());
  }

  saveJsonApiResponse(resource: D): Observable<JsonApiResource<D, MD>> {
    return this.save(resource).pipe(this.getJsonApiMapper());
  }

  /**
   * @deprecated Use updateDtoResponse or updateJsonApiResponse or your own implementation; This method will be protected in the future;
   * @param resource
   */
  update(resource: D): any {
    return this.httpClient?.put(`${this.url}`, resource, { headers: this.headers })
  }

  updateDtoResponse(resource: D): Observable<D> {
    return this.update(resource).pipe(this.getDtoMapper());
  }

  updateJsonApiResponse(resource: D): Observable<JsonApiResource<D, MD>> {
    return this.update(resource).pipe(this.getJsonApiMapper());
  }

  /**
   * @deprecated Use patchDtoResponse or patchJsonApiResponse or your own implementation; This method will be protected in the future;
   * @param uuid
   * @param patch
   */
  patch(uuid: string, patch: Partial<D>): any {
    return this.httpClient?.patch(`${this.url}/${uuid}`, patch, { headers: this.headers})
  }

  patchDtoResponse(uuid: string, patch: Partial<D>): Observable<D> {
    return this.patch(uuid, patch).pipe(this.getDtoMapper());
  }

  patchJsonApiResponse(uuid: string, patch:  Partial<D>): Observable<JsonApiResource<D, MD>> {
    return this.patch(uuid, patch).pipe(this.getJsonApiMapper());
  }

  /**
   * @deprecated Use removeDtoResponse or removeJsonApiResponse or your own implementation; This method will be protected in the future;
   * @param uuid
   */
  remove(uuid: string): any {
    return this.httpClient?.delete(`${this.url}/${uuid}`, {headers: this.headers})
  }

  removeDtoResponse(resource: string): Observable<D> {
    return this.remove(resource).pipe(this.getDtoMapper());
  }

  removeJsonApiResponse(resource: string): Observable<JsonApiResource<D, MD>> {
    return this.remove(resource).pipe(this.getJsonApiMapper());
  }

}
